import React from "react";
import { Box } from "@mui/joy";
import {
  FormatBoldIcon as FormatBold,
  FormatItalicIcon as FormatItalic,
  LinkIcon,
  SmartButtonOutlinedIcon as SmartButtonOutlined,
} from "../../../../styles/StyledMUI";
import IconButtonCTA from "../../../common/IconButtonCTA";
import type {
  MailPlaceholderDefinition,
  MailPlaceholderValueType,
} from "api/mailTemplates/MailTemplate.typed";
import {
  codeFontFamily,
  colorTokens,
  mailEditorTextareaSx,
  textTokens,
  withAlpha,
} from "../../../../styles/globalStyles";
import {
  applyMailAnchorHrefAtSelection,
  applyMailButtonLinkAtSelection,
  applyMailButtonReplacingPhChipAtSelection,
  applyMailLinkAtSelection,
  applyMailLinkReplacingPhChipAtSelection,
  execBoldInMailEditor,
  execItalicInMailEditor,
  findMailAnchorAtSelection,
  findMailPhChipAtSelection,
  insertMailPhChipAtCaretInContentEditable,
  insertPlainTextAtCaretInContentEditable,
  collapseCaretToEnd,
  toggleMailPhChipStyleAtSelection,
  tryDeleteMailPhChipFromKeyboard,
  trySelectMailPhChipFromPointerEvent,
} from "../mailContentEditable";
import CuratedEmojiPicker from "../../../common/CuratedEmojiPicker";
import {
  mailPlaceholderDragOverHandler,
  mailPlaceholderKeyFromDataTransfer,
} from "../mailEditorDnD";
import {
  DEFAULT_MAIL_EDITOR_LINK_HREF,
  MAIL_EDITOR_PH_CHIP_CLASS,
  MAIL_EDITOR_PH_CHIP_SELECTED_CLASS,
  mergeAdjacentTextParts,
  mailLinePartsForSubjectField,
  parseHtmlToMailLineParts,
  serializeMailLinePartsToEditorInnerHtml,
} from "../mailLineParts";
import type { MailLinePart, SerializeMailLinePartsToEditorOptions } from "../mailLineParts";
import { MAIL_BODY_RICH_FIELD_KEY } from "../mailBodyBlocks";
import { MAIL_ENVELOPE_CONTROL_HEIGHT_PX } from "../mailEditorShared";
import {
  mailLinkHrefPlaceholderChips,
  mailPlaceholderBodyHrefScheme,
  mailPlaceholderKeyAllowedForValueTypes,
  mailPlaceholderValueTypesForConditionKeyField,
  mailPlaceholderValueTypesForRichBody,
  mailPlaceholderValueTypesForSubject,
} from "../mailPlaceholderContext";
import MailEditorLinkUrlModal from "./MailEditorLinkUrlModal";

export type MailRichLineEditorProps = {
  parts: MailLinePart[];
  onChange: (parts: MailLinePart[]) => void;
  canEdit: boolean;
  blockId: string;
  ariaLabel: string;
  setFieldRef: (key: string, el: HTMLElement | null) => void;
  trackFocus: (blockId: string, field: string) => void;
  minRowsText?: number;
  // Ref/focus field id when one block hosts several contenteditables (e.g. conditional if/else).
  editorFieldId?: string;
  variant?: "default" | "conditionKey" | "subject";
  placeholder?: string;
  presentablePhLabel?: (key: string) => string;
  placeholderCatalog?: MailPlaceholderDefinition[];
  placeholderValueTypes?: ReadonlySet<MailPlaceholderValueType>;
};

export default function MailRichLineEditor(props: MailRichLineEditorProps) {
  const {
    parts,
    onChange,
    canEdit,
    blockId,
    ariaLabel,
    setFieldRef,
    trackFocus,
    minRowsText = 2,
    editorFieldId = MAIL_BODY_RICH_FIELD_KEY,
    variant = "default",
    presentablePhLabel,
    placeholderCatalog,
    placeholder,
    placeholderValueTypes,
  } = props;
  const isConditionKey = variant === "conditionKey";
  const isSubject = variant === "subject";
  const isCompactLine = isConditionKey || isSubject;
  const editorSerializeOpts = React.useMemo(
    (): SerializeMailLinePartsToEditorOptions | undefined =>
      presentablePhLabel ? { presentablePhLabel } : undefined,
    [presentablePhLabel],
  );
  const editorRef = React.useRef<HTMLDivElement | null>(null);
  const [linkUrlModalOpen, setLinkUrlModalOpen] = React.useState(false);
  const [linkModalInitialHref, setLinkModalInitialHref] = React.useState(
    DEFAULT_MAIL_EDITOR_LINK_HREF,
  );
  const [linkModalForButton, setLinkModalForButton] = React.useState(false);
  const linkApplyRangeRef = React.useRef<Range | null>(null);
  const linkApplyAnchorRef = React.useRef<HTMLAnchorElement | null>(null);
  const linkApplyPhChipRef = React.useRef<HTMLElement | null>(null);
  const isInternalEditRef = React.useRef(false);
  const lastSerializedRef = React.useRef(
    serializeMailLinePartsToEditorInnerHtml(parts, editorSerializeOpts),
  );
  const refKey = `${blockId}:${editorFieldId}`;
  const phChipSelectFrameRef = React.useRef(0);

  const syncPhChipSelectedClass = React.useCallback(() => {
    const root = editorRef.current;

    if (!root) {
      return;
    }

    root.querySelectorAll<HTMLElement>(`.${MAIL_EDITOR_PH_CHIP_CLASS}`).forEach((node) => {
      node.classList.remove(MAIL_EDITOR_PH_CHIP_SELECTED_CLASS);
    });

    const sel = window.getSelection();

    if (!sel?.anchorNode || !root.contains(sel.anchorNode)) {
      return;
    }

    const chip = findMailPhChipAtSelection(root);

    if (chip) {
      chip.classList.add(MAIL_EDITOR_PH_CHIP_SELECTED_CLASS);
    }
  }, []);

  React.useEffect(() => {
    if (!canEdit) {
      return;
    }

    const onSelectionChange = () => {
      cancelAnimationFrame(phChipSelectFrameRef.current);
      phChipSelectFrameRef.current = requestAnimationFrame(() => syncPhChipSelectedClass());
    };

    document.addEventListener("selectionchange", onSelectionChange);

    return () => {
      cancelAnimationFrame(phChipSelectFrameRef.current);
      document.removeEventListener("selectionchange", onSelectionChange);
    };
  }, [canEdit, syncPhChipSelectedClass]);

  const flushDomToParts = React.useCallback(() => {
    const el = editorRef.current;

    if (!el) {
      return;
    }

    const parsed = mergeAdjacentTextParts(parseHtmlToMailLineParts(el.innerHTML));
    const nextParts = isSubject ? mailLinePartsForSubjectField(parsed) : parsed;
    const ser = serializeMailLinePartsToEditorInnerHtml(nextParts, editorSerializeOpts);

    if (el.innerHTML !== ser && /\{\{\.[A-Za-z0-9_]+\}\}/.test(el.textContent ?? "")) {
      el.innerHTML = ser;
      collapseCaretToEnd(el);
    }

    isInternalEditRef.current = true;
    lastSerializedRef.current = ser;
    onChange(nextParts);
  }, [editorSerializeOpts, isSubject, onChange]);

  const setEditorRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      editorRef.current = node;
      setFieldRef(refKey, node);
    },
    [refKey, setFieldRef],
  );

  React.useEffect(() => {
    return () => setFieldRef(refKey, null);
  }, [refKey, setFieldRef]);

  React.useEffect(() => {
    const el = editorRef.current;

    if (!el || !canEdit) {
      return;
    }

    if (isInternalEditRef.current) {
      isInternalEditRef.current = false;

      return;
    }

    const incoming = serializeMailLinePartsToEditorInnerHtml(parts, editorSerializeOpts);

    if (el.innerHTML !== incoming) {
      el.innerHTML = incoming;
    }

    lastSerializedRef.current = incoming;
  }, [parts, canEdit, editorSerializeOpts]);

  const handleInput = React.useCallback(() => {
    flushDomToParts();
  }, [flushDomToParts]);

  const runBold = React.useCallback(() => {
    if (!canEdit) {
      return;
    }

    const el = editorRef.current;

    if (!el) {
      return;
    }

    if (toggleMailPhChipStyleAtSelection(el, "bold")) {
      flushDomToParts();

      return;
    }

    execBoldInMailEditor(el);
    flushDomToParts();
  }, [canEdit, flushDomToParts]);

  const insertEmoji = React.useCallback(
    (emoji: string) => {
      if (!canEdit) {
        return;
      }

      const el = editorRef.current;

      if (!el) {
        return;
      }

      insertPlainTextAtCaretInContentEditable(el, emoji);
      flushDomToParts();
    },
    [canEdit, flushDomToParts],
  );

  const runItalic = React.useCallback(() => {
    if (!canEdit) {
      return;
    }

    const el = editorRef.current;

    if (!el) {
      return;
    }

    if (toggleMailPhChipStyleAtSelection(el, "italic")) {
      flushDomToParts();

      return;
    }

    execItalicInMailEditor(el);
    flushDomToParts();
  }, [canEdit, flushDomToParts]);

  const openLinkUrlModal = React.useCallback(() => {
    if (!canEdit) {
      return;
    }

    setLinkModalForButton(false);
    linkApplyPhChipRef.current = null;
    linkApplyRangeRef.current = null;
    linkApplyAnchorRef.current = null;

    const el = editorRef.current;
    let initial = DEFAULT_MAIL_EDITOR_LINK_HREF;

    if (el) {
      const sel = window.getSelection();

      if (sel && sel.rangeCount > 0 && el.contains(sel.anchorNode)) {
        try {
          linkApplyRangeRef.current = sel.getRangeAt(0).cloneRange();
        } catch {
          linkApplyRangeRef.current = null;
        }
      }

      const a = findMailAnchorAtSelection(el);

      if (a && el.contains(a)) {
        linkApplyAnchorRef.current = a;
        initial = a.getAttribute("href")?.trim() || DEFAULT_MAIL_EDITOR_LINK_HREF;
      }
    }

    setLinkModalInitialHref(initial);
    setLinkUrlModalOpen(true);
  }, [canEdit]);

  const confirmApplyLink = React.useCallback(
    (entered: string) => {
      const el = editorRef.current;

      if (!el) {
        return;
      }

      el.focus();

      const savedRange = linkApplyRangeRef.current;
      const savedAnchor = linkApplyAnchorRef.current;
      const savedChip = linkApplyPhChipRef.current;

      linkApplyRangeRef.current = null;
      linkApplyAnchorRef.current = null;
      linkApplyPhChipRef.current = null;

      if (linkModalForButton) {
        applyMailButtonReplacingPhChipAtSelection(el, entered, savedChip);
        flushDomToParts();

        return;
      }

      if (savedAnchor && el.contains(savedAnchor)) {
        const href = entered.trim() || DEFAULT_MAIL_EDITOR_LINK_HREF;

        savedAnchor.setAttribute("href", href);
        flushDomToParts();

        return;
      }

      if (savedRange) {
        try {
          const s = window.getSelection();

          s?.removeAllRanges();
          s?.addRange(savedRange);
        } catch {
          // Range no longer valid in the editor DOM.
        }
      }

      if (applyMailLinkReplacingPhChipAtSelection(el, entered)) {
        flushDomToParts();

        return;
      }

      if (applyMailAnchorHrefAtSelection(el, entered)) {
        flushDomToParts();

        return;
      }

      applyMailLinkAtSelection(el, entered);
      flushDomToParts();
    },
    [flushDomToParts, linkModalForButton],
  );

  const applyButton = React.useCallback(() => {
    if (!canEdit) {
      return;
    }

    const el = editorRef.current;

    if (!el) {
      return;
    }

    const chip = findMailPhChipAtSelection(el);

    if (chip && el.contains(chip)) {
      linkApplyRangeRef.current = null;
      linkApplyAnchorRef.current = null;
      linkApplyPhChipRef.current = chip;
      setLinkModalForButton(true);
      setLinkModalInitialHref(DEFAULT_MAIL_EDITOR_LINK_HREF);
      setLinkUrlModalOpen(true);

      return;
    }

    applyMailButtonLinkAtSelection(el);
    flushDomToParts();
  }, [canEdit, flushDomToParts]);

  const editorMinHeight = isConditionKey
    ? MAIL_ENVELOPE_CONTROL_HEIGHT_PX
    : isSubject
      ? MAIL_ENVELOPE_CONTROL_HEIGHT_PX
      : Math.max(72, minRowsText * 22);
  const dropAllowedTypes =
    placeholderValueTypes ??
    (isConditionKey
      ? mailPlaceholderValueTypesForConditionKeyField()
      : isSubject
        ? mailPlaceholderValueTypesForSubject()
        : mailPlaceholderValueTypesForRichBody());
  const serialized = serializeMailLinePartsToEditorInnerHtml(parts, editorSerializeOpts);

  const compactLineSx = isConditionKey
    ? {
        whiteSpace: "nowrap" as const,
        overflowX: "auto" as const,
        overflowY: "hidden" as const,
        wordBreak: "normal" as const,
      }
    : isSubject
      ? {
          py: 0.35,
          px: 1,
          fontSize: 13,
          lineHeight: 1.35,
          whiteSpace: "pre-wrap" as const,
          overflowX: "hidden" as const,
          wordBreak: "break-word" as const,
        }
      : { wordBreak: "break-word" as const };

  const placeholderSx =
    placeholder && placeholder.length > 0
      ? {
          "&[data-placeholder]:empty::before": {
            content: "attr(data-placeholder)",
            color: colorTokens.text.placeholder,
            pointerEvents: "none",
          },
        }
      : {};

  const chipSurfaceSx = {
    [`& .${MAIL_EDITOR_PH_CHIP_CLASS}`]: {
      display: "inline-flex",
      alignItems: "center",
      verticalAlign: "baseline",
      maxWidth: "100%",
      boxSizing: "border-box" as const,
      px: 0.75,
      py: 0.125,
      mx: "2px",
      borderRadius: "999px",
      fontSize: "0.82em",
      fontFamily: codeFontFamily,
      lineHeight: 1.35,
      color: colorTokens.accent.primary,
      bgcolor: withAlpha(colorTokens.accent.primary, 0.14),
      border: `1px solid ${withAlpha(colorTokens.accent.primary, 0.42)}`,
      userSelect: "none",
      cursor: "pointer",
      WebkitBoxDecorationBreak: "clone",
      boxDecorationBreak: "clone" as const,
    },
    [`& .${MAIL_EDITOR_PH_CHIP_CLASS}.${MAIL_EDITOR_PH_CHIP_SELECTED_CLASS}`]: {
      zIndex: 1,
      position: "relative" as const,
      color: textTokens.color.inverse,
      bgcolor: withAlpha(textTokens.color.title, 0.35),
      borderColor: textTokens.color.title,
      outline: `2px solid ${textTokens.color.title}`,
      outlineOffset: "1px",
      boxShadow: `0 0 0 3px ${withAlpha(textTokens.color.title, 0.35)}`,
    },
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: isCompactLine ? 0 : 0.75,
        maxWidth: "100%",
      }}
    >
      {!isCompactLine ? (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
          <IconButtonCTA
            aria-label="Gras sur la sélection ou sur la puce variable"
            size="sm"
            ctaVariant="accent"
            disabled={!canEdit}
            onClick={runBold}
          >
            <FormatBold fontSize="small" />
          </IconButtonCTA>
          <IconButtonCTA
            aria-label="Italique sur la sélection ou sur la puce variable"
            size="sm"
            ctaVariant="accent"
            disabled={!canEdit}
            onClick={runItalic}
          >
            <FormatItalic fontSize="small" />
          </IconButtonCTA>
          <IconButtonCTA
            aria-label="Lien : remplace la puce variable par un lien dont le texte est le jeton, ou entoure la sélection"
            size="sm"
            ctaVariant="accent"
            disabled={!canEdit}
            onClick={openLinkUrlModal}
          >
            <LinkIcon fontSize="small" />
          </IconButtonCTA>
          <IconButtonCTA
            aria-label="Bouton jaune : remplace la puce variable ou entoure la sélection"
            size="sm"
            ctaVariant="accent"
            disabled={!canEdit}
            onClick={applyButton}
          >
            <SmartButtonOutlined fontSize="small" />
          </IconButtonCTA>
          {canEdit ? (
            <CuratedEmojiPicker onPick={insertEmoji} menuZIndex={2500} triggerSizePx={32} />
          ) : null}
        </Box>
      ) : null}

      {canEdit ? (
        <Box
          ref={setEditorRef}
          contentEditable
          suppressContentEditableWarning
          role="textbox"
          aria-multiline={isCompactLine ? "false" : "true"}
          aria-label={ariaLabel}
          data-placeholder={placeholder}
          spellCheck
          onInput={handleInput}
          onKeyDown={(e) => {
            if (isCompactLine && e.key === "Enter") {
              e.preventDefault();

              return;
            }

            if (!canEdit || (e.key !== "Backspace" && e.key !== "Delete")) {
              return;
            }

            const el = editorRef.current;

            if (!el) {
              return;
            }

            if (tryDeleteMailPhChipFromKeyboard(el, e.key)) {
              e.preventDefault();
              flushDomToParts();
            }
          }}
          onMouseDownCapture={(e) => {
            const el = editorRef.current;

            if (!el || !canEdit) {
              return;
            }

            if (trySelectMailPhChipFromPointerEvent(el, e)) {
              requestAnimationFrame(() => syncPhChipSelectedClass());
            }
          }}
          onFocus={() => trackFocus(blockId, editorFieldId)}
          onDragOver={mailPlaceholderDragOverHandler}
          onDrop={(e: React.DragEvent) => {
            if (!canEdit) {
              return;
            }

            const key = mailPlaceholderKeyFromDataTransfer(e.dataTransfer);

            if (!key) {
              return;
            }

            e.preventDefault();

            if (placeholderCatalog && placeholderCatalog.length > 0) {
              if (
                !mailPlaceholderKeyAllowedForValueTypes(placeholderCatalog, key, dropAllowedTypes)
              ) {
                return;
              }
            }

            const el = editorRef.current;

            if (!el) {
              return;
            }

            insertMailPhChipAtCaretInContentEditable(
              el,
              key,
              "none",
              presentablePhLabel?.(key),
              isSubject ? undefined : mailPlaceholderBodyHrefScheme(key, placeholderCatalog),
            );
            flushDomToParts();
          }}
          sx={[
            ...(Array.isArray(mailEditorTextareaSx)
              ? mailEditorTextareaSx
              : [mailEditorTextareaSx]),
            chipSurfaceSx,
            placeholderSx,
            {
              minHeight: editorMinHeight,
              outline: "none",
              cursor: "text",
              lineHeight: 1.5,
              ...compactLineSx,
              "&:focus-visible": { boxShadow: "0 0 0 2px rgba(95, 168, 255, 0.35)" },
            },
          ]}
        />
      ) : (
        <Box
          data-placeholder={placeholder}
          sx={[
            ...(Array.isArray(mailEditorTextareaSx)
              ? mailEditorTextareaSx
              : [mailEditorTextareaSx]),
            chipSurfaceSx,
            placeholderSx,
            {
              minHeight: editorMinHeight,
              lineHeight: 1.5,
              ...compactLineSx,
              opacity: 0.85,
            },
          ]}
          dangerouslySetInnerHTML={{ __html: serialized || "&nbsp;" }}
        />
      )}
      <MailEditorLinkUrlModal
        open={linkUrlModalOpen}
        onClose={() => {
          setLinkUrlModalOpen(false);
          setLinkModalForButton(false);
          linkApplyRangeRef.current = null;
          linkApplyAnchorRef.current = null;
          linkApplyPhChipRef.current = null;
        }}
        initialHref={linkModalInitialHref}
        onConfirm={confirmApplyLink}
        title={linkModalForButton ? "Adresse du bouton" : "Adresse du lien"}
        urlPlaceholderChips={
          placeholderCatalog && placeholderCatalog.length > 0
            ? mailLinkHrefPlaceholderChips(placeholderCatalog)
            : []
        }
        labelLookupCatalog={placeholderCatalog}
        canEdit={canEdit}
      />
    </Box>
  );
}
